<template>
  <div>
    <editor-menu-bar
      v-if="!readonly"
      v-slot="{ commands, isActive, getMarkAttrs }"
      :editor="editor"
    >
      <div>
        <div
          v-if="linkMenuIsActive"
          class="m-4"
        >
          <b-row>
            <b-col md="5">
              <b-form-group label="Select a page or enter a URL.">
                <pages-select-menu
                  input-class="rounded-[4px]"
                  :others="[{ id: 'custom-link', title: 'Custom Link', custom_link:'' }]"
                  other-title="Other:"
                  @selectPage="(item) => (selectedPage = item)"
                  @selectOther="(item) => (selectedPage = item)"
                >
                  <template
                    v-if="selectedPage"
                    slot="placeholder"
                  >
                    <div class="px-3">
                      {{ selectedPage.title }}
                    </div>
                  </template>
                </pages-select-menu>
              </b-form-group>
        
              <b-form-group
                v-if="selectedPage && selectedPage.id === 'custom-link'"
                class="position-relative"
              >
                <b-input
                  ref="linkInput"
                  v-model="selectedPage.custom_link"
                  type="url"
                  autofocus
                  placeholder="https://"
                  @keydown.esc="hideLinkMenu"
                />
              </b-form-group>

              <div class="d-flex">
                <b-btn
                  class="btn-icon mr-2"
                  variant="primary"
                  @click="setLinkUrl(commands.link)"
                >
                  <i class="fa fa-check" />
                </b-btn>

                <b-btn
                  class="btn-icon"
                  @click.stop="setLinkUrl(commands.link, null)"
                >
                  <i class="fa fa-times" />
                </b-btn>
              </div>
            </b-col>
          </b-row>
        </div>

        <div class="rounded-top border p-2">
          <b-row
            class="menubar"
            no-gutters
          >
            <b-col cols="2">
              <b-btn-group>
                <b-btn
                  variant="menubar-button border-0"
                  :class="{ 'is-active': isActive.bold() }"
                  @click="commands.bold"
                >
                  <i class="fa fa-bold" />
                </b-btn>
                <b-btn
                  variant="menubar-button border-0"
                  :class="{ 'is-active': isActive.italic() }"
                  @click="commands.italic"
                >
                  <i class="fa fa-italic" />
                </b-btn>
                <b-btn
                  variant="menubar-button border-0"
                  :class="{ 'is-active': isActive.underline() }"
                  @click="commands.underline"
                >
                  <i class="fa fa-underline" />
                </b-btn>
                <b-btn
                  variant="menubar-button border-0"
                  :class="{ 'is-active': isActive.link() }"
                  @click="showLinkMenu(getMarkAttrs('link'))"
                >
                  <i class="fa fa-link" />
                </b-btn>
              </b-btn-group>
            </b-col>
          </b-row>
        </div>
      </div>
    </editor-menu-bar>

    <div class="border border-top-0">
      <editor-content
        ref="editor"
        style="overflow-wrap: anywhere"
        class="success-message p-4"
        :class="{'bg-[#e9ecef]': readonly}"
        :editor="editor"
      />
    </div>
  </div>
</template>

<script>
import { Editor } from '@tiptap/core'
import { EditorContent } from '@tiptap/vue-2'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Blockquote from '@tiptap/extension-blockquote'
import BulletList from '@tiptap/extension-bullet-list'
import CodeBlock from '@tiptap/extension-code-block'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import Bold from '@tiptap/extension-bold'
import Code from '@tiptap/extension-code'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import Strike from '@tiptap/extension-strike'
import Underline from '@tiptap/extension-underline'
import History from '@tiptap/extension-history'
import { mapGetters } from 'vuex'
import PagesPaginationMixin from '../../../editor/mixins/PagesPaginationMixin'
import PagesSelectMenu from '@/components/editor/components/pagesSelectMenu/PagesSelectMenu'

const EditorMenuBar = {
  name: 'EditorMenuBarCompat',
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  render () {
    const editor = this.editor

    if (!editor) {
      return null
    }

    // Explicit commands that map old-style names to the new v3 chain API
    const explicitCommands = {
      bold: () => editor.chain().focus().toggleBold().run(),
      italic: () => editor.chain().focus().toggleItalic().run(),
      underline: () => editor.chain().focus().toggleUnderline().run(),
      strike: () => editor.chain().focus().toggleStrike().run(),
      // link: set or unset link on the current selection
      link: (attrs) => {
        const chain = editor.chain().focus().extendMarkRange('link')
        if (!attrs) {
          return chain.unsetLink().run()
        }
        return chain.setLink(attrs).run()
      }
    }

    const baseCommands = editor.commands || {}

    const commands = new Proxy(explicitCommands, {
      get (target, key) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          return target[key]
        }

        const commandFn = baseCommands[key]

        if (typeof commandFn === 'function') {
          return (...args) => commandFn(...args)
        }

        return (...args) => {
          const chain = editor.chain().focus()
          if (typeof chain[key] === 'function') {
            return chain[key](...args).run()
          }
          return null
        }
      }
    })

    const isActive = new Proxy({}, {
      get (_target, key) {
        return (attrs) => editor.isActive(key, attrs)
      }
    })

    const getMarkAttrs = (name) => {
      try {
        return editor.getAttributes(name) || {}
      } catch (e) {
        return {}
      }
    }

    const slot = this.$scopedSlots.default

    if (!slot) {
      return null
    }

    const children = slot({
      editor,
      commands,
      isActive,
      getMarkAttrs,
      getNodeAttrs: getMarkAttrs
    })

    return Array.isArray(children) ? children[0] : children
  }
}

export default {
  name: 'Content',

  components: {
    PagesSelectMenu,
    EditorContent,
    EditorMenuBar
  },

  mixins: [PagesPaginationMixin],

  props: {
    value: {
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      selectedPage: null,
      linkUrl: null,
      linkMenuIsActive: false,
      keepInBounds: true,
      editor: new Editor({
        extensions: [
          Document,
          Paragraph,
          Text,
          Blockquote,
          BulletList,
          CodeBlock,
          HardBreak,
          Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
          ListItem,
          OrderedList,
          TaskList,
          TaskItem,
          Link.configure({
            openOnClick: false
          }),
          Bold,
          Code,
          Italic,
          Strike,
          Underline,
          History
        ],
        onUpdate: ({ editor }) => {
          const content = editor.getHTML()
          this.$emit('input', content)
        },
        parseOptions: {
          preserveWhitespace: 'full'
        },
        editable: !this.readonly,
        content: this.value
      })
    }
  },

  computed: {
    ...mapGetters('pages', {
      pages: 'getPages'
    })
  },

  mounted () {
    if (this.readonly && this.editor && typeof this.editor.setEditable === 'function') {
      this.editor.setEditable(false)
    }
  },

  beforeDestroy () {
    // this.editor.destroy()
  },

  methods: {
    async onOpen () {
      setTimeout(() => {
        document
          .querySelector('.vs__dropdown-menu')
          .addEventListener('scroll', this.scrollLoadingPages)
      })
    },
    onClose () {
      this.searching = false
      document
        .querySelector('.vs__dropdown-menu')
        .removeEventListener('scroll', this.scrollLoadingPages)
    },
    showLinkMenu (attrs) {
      this.selectedPage = attrs.href ? JSON.parse(attrs.href) : null
      this.linkMenuIsActive = true
    },
    hideLinkMenu () {
      this.selectedPage = null
      this.linkMenuIsActive = false
    },
    setLinkUrl (command, value = this.selectedPage) {
      if (!value) {
        // unset link
        command(null)
        this.hideLinkMenu()
        return
      }

      command({ href: JSON.stringify(value) })
      this.hideLinkMenu()
    }
  }
}
</script>

<style lang="scss">
.success-message {
  > .ProseMirror {
    min-height: 200px;
  }

  a {
    color: #4f83e3ff;
  }

  p {
    color: #6e747a;
    font-size: 16px;
  }

  strong {
    color: #000;
  }
}
</style>
